// frontend/src/components/common/ModalConfirmacion.jsx
// Modal de confirmación reutilizable — reemplaza window.confirm() del browser
// Uso: const ok = await confirmar({ titulo, mensaje, labelConfirmar, variante })
// Con `selector` ({ opciones: [{ valor, label, descripcion }], valorInicial }) el modal
// pide elegir una opción antes de confirmar y resuelve con el `valor` elegido (en vez
// de `true`); cancelar sigue resolviendo `false`.
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { registrarConfirmar } from '../../services/confirmBridge';

// ─── Context ──────────────────────────────────────────────────────────────────

const ConfirmContext = createContext(null);

const OPCIONES_DEFAULT = {
  titulo:         'Confirmar acción',
  mensaje:        '',
  labelConfirmar: 'Confirmar',
  labelCancelar:  'Cancelar',
  variante:       'default', // 'default' | 'danger' | 'warning'
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ConfirmProvider = ({ children }) => {
  const [estado, setEstado] = useState({ visible: false, opciones: OPCIONES_DEFAULT });
  const resolveRef = useRef(null);

  const confirmar = useCallback((opciones = {}) => {
    return new Promise((resolve) => {
      setEstado({ visible: true, opciones: { ...OPCIONES_DEFAULT, ...opciones } });
      resolveRef.current = resolve;
    });
  }, []);

  // Exponer `confirmar` a la capa de servicios (apiClient) para confirmaciones globales.
  useEffect(() => {
    registrarConfirmar(confirmar);
  }, [confirmar]);

  const resolver = useCallback((valor) => {
    setEstado(s => ({ ...s, visible: false }));
    // Pequeño delay para que termine la animación antes de liberar la promesa
    setTimeout(() => resolveRef.current?.(valor), 120);
  }, []);

  // Soporte de teclado
  useEffect(() => {
    if (!estado.visible) return;
    const onKey = (e) => {
      if (e.key === 'Escape') resolver(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [estado.visible, resolver]);

  return (
    <ConfirmContext.Provider value={{ confirmar }}>
      {children}
      {estado.visible && (
        <ModalUI
          opciones={estado.opciones}
          onConfirmar={(valor = true) => resolver(valor)}
          onCancelar={() => resolver(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm debe usarse dentro de ConfirmProvider');
  return ctx;
};

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95) translateY(-8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.overlayBackdrop};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
  animation: ${fadeIn} 0.15s ease both;
  padding: 16px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.colors.shadowModal};
  width: 100%;
  max-width: 420px;
  overflow: hidden;
  animation: ${scaleIn} 0.18s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const VariantBar = styled.div`
  height: 3px;
  background: ${({ $variante, theme }) => {
    if ($variante === 'danger')  return theme.colors.danger;
    if ($variante === 'warning') return theme.colors.warning;
    return theme.colors.primary;
  }};
`;

const Body = styled.div`
  padding: 24px 24px 20px;
`;

const Icono = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  font-size: 20px;
  background: ${({ $variante, theme }) => {
    if ($variante === 'danger')  return `${theme.colors.danger}1a`;
    if ($variante === 'warning') return `${theme.colors.warning}1a`;
    return `${theme.colors.primary}1a`;
  }};
  color: ${({ $variante, theme }) => {
    if ($variante === 'danger')  return theme.colors.danger;
    if ($variante === 'warning') return theme.colors.warning;
    return theme.colors.primary;
  }};
`;

const Titulo = styled.h3`
  margin: 0 0 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.3;
`;

const Mensaje = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.55;
`;

const Opciones = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const Opcion = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ $activa, theme }) => ($activa ? theme.colors.primary : theme.colors.border)};
  background: ${({ $activa, theme }) => ($activa ? theme.colors.primaryAlpha : 'transparent')};
  cursor: pointer;

  input { margin-top: 3px; flex-shrink: 0; }
  strong {
    display: block;
    font-size: ${({ theme }) => theme.typography.fontSize.md};
    color: ${({ theme }) => theme.colors.text};
  }
  small {
    display: block;
    margin-top: 2px;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.45;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 24px 20px;
`;

const BtnBase = styled.button`
  padding: 8px 18px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
  min-width: 90px;
  border: 1px solid transparent;
`;

const BtnCancelar = styled(BtnBase)`
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-color: ${({ theme }) => theme.colors.border};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const BtnConfirmar = styled(BtnBase)`
  color: ${({ theme }) => theme.colors.onPrimary};
  background: ${({ $variante, theme }) => {
    if ($variante === 'danger')  return theme.colors.danger;
    if ($variante === 'warning') return theme.colors.warning;
    return theme.colors.primary;
  }};
  border-color: transparent;

  &:hover {
    opacity: 0.88;
  }
  &:active {
    opacity: 0.75;
  }
`;

// ─── UI del modal ─────────────────────────────────────────────────────────────

const ICONOS_VARIANTE = {
  danger:  'bi-exclamation-triangle-fill',
  warning: 'bi-exclamation-circle-fill',
  default: 'bi-question-circle-fill',
};

const ModalUI = ({ opciones, onConfirmar, onCancelar }) => {
  const { titulo, mensaje, labelConfirmar, labelCancelar, variante, selector } = opciones;
  const [elegida, setElegida] = useState(selector?.valorInicial ?? selector?.opciones?.[0]?.valor);

  return (
    <Backdrop onClick={e => e.target === e.currentTarget && onCancelar()}>
      <Card role="dialog" aria-modal="true" aria-labelledby="confirm-titulo">
        <VariantBar $variante={variante} />
        <Body>
          <Icono $variante={variante}>
            <i className={`bi ${ICONOS_VARIANTE[variante] || ICONOS_VARIANTE.default}`} />
          </Icono>
          <Titulo id="confirm-titulo">{titulo}</Titulo>
          {mensaje && <Mensaje>{mensaje}</Mensaje>}
          {selector?.opciones?.length > 0 && (
            <Opciones role="radiogroup">
              {selector.opciones.map((op) => (
                <Opcion key={op.valor} $activa={elegida === op.valor}>
                  <input
                    type="radio"
                    name="confirm-selector"
                    checked={elegida === op.valor}
                    onChange={() => setElegida(op.valor)}
                  />
                  <span>
                    <strong>{op.label}</strong>
                    {op.descripcion && <small>{op.descripcion}</small>}
                  </span>
                </Opcion>
              ))}
            </Opciones>
          )}
        </Body>
        <Footer>
          <BtnCancelar onClick={onCancelar}>{labelCancelar}</BtnCancelar>
          <BtnConfirmar $variante={variante} onClick={() => onConfirmar(selector ? elegida : true)} autoFocus>
            {labelConfirmar}
          </BtnConfirmar>
        </Footer>
      </Card>
    </Backdrop>
  );
};
